import axios from 'axios';

// WHERE THIS BUILD'S SERVER IS, and whether this build is the demo.
//
// The app is served under a base path (Vite `base` -> import.meta.env.BASE_URL): "/"
// for the real app at the root of app.noticeia.com, "/demo/" for the demo, which is a
// second backend from a second checkout behind the same domain. Its API is under that
// same prefix — /demo/api/notice/... — so every request has to carry it.
//
// TWO WAYS IT GETS CARRIED, because the app asks for two kinds of thing.
//
// Requests go through axios, and axios has one place to say this: `baseURL` is
// prepended to any URL that is not absolute, and a path beginning with a single "/"
// is not absolute by axios's reckoning. So the app's hundred and eighty call sites
// keep writing '/api/notice/...' and every one of them follows the base. That is the
// whole reason this is not a hundred and eighty edits.
//
// PICTURES AND MESHES DO NOT GO THROUGH AXIOS. A URL that ends up in an <img src> or
// in a three.js loader is a string the browser fetches itself, and nothing intercepts
// it — those are the ones that call apiUrl() by hand. They are few and they are all in
// URL builders (threeDApi's model/picture urls, the listing-photo proxy).
export const API_PREFIX = (import.meta.env.BASE_URL || '/').replace(/\/+$/, '');

// Prefix a server path with this build's base. For URLs the browser fetches on its
// own; anything going through axios already has it.
export const apiUrl = (path: string) => API_PREFIX + path;

// THE DEMO BUILD. Set at build time (VITE_NOTICE_DEMO=1), not sniffed from the URL:
// the demo is a build, deliberately frozen at a version, and a page that decided what
// it was from the address bar would be one proxy rule away from lying about it.
//
// What it changes, all of it in the pages rather than in the server: the Tests and
// Admin tabs are not there, and everything that would start a run is disabled. The
// server it talks to refuses the same things on its own — see backend/appenv.py — so
// this is the demo being honest about itself, not the demo being safe.
export const IS_DEMO = import.meta.env.VITE_NOTICE_DEMO === '1';

// AND A BUILD THAT MAY LOOK BUT NOT PRESS, which the demo is one of and is no longer the only
// one. The sandbox refuses to spend at the outbound edge exactly as the demo does — it is the
// same NOTICE_NO_SPEND, set in its own start script — so every run button on it was a button
// that reached the server and came back refused. A dead button that looks alive is the app
// lying about itself for the length of one request.
//
// SEPARATE FROM IS_DEMO, because they answer different questions. IS_DEMO says "this build is
// the demo": no Tests tab, no Admin, nothing written down, a Phase 6 standing only on
// validated sub-steps. This says one thing only — the run buttons are off — and the sandbox
// wants that one thing and none of the rest: it keeps its tabs, its admin page and its
// writes, because a sandbox that cannot save what you did to it is not one.
//
// The demo implies it, so nothing that reads this has to remember to ask for both.
export const IS_LOCKED = IS_DEMO || import.meta.env.VITE_NOTICE_LOCKED === '1';

// AND THE SANDBOX ITSELF, by name — set in scripts/sandbox-dev.sh beside the lock.
//
// A THIRD FLAG AND NOT A THIRD MEANING FOR THE SECOND. IS_LOCKED says one thing, "the run
// buttons are off", and the demo says it too; this says WHICH APP this is, and only the
// sandbox says it. What hangs off it is the sandbox's own furniture: no Tests tab and no
// Database tab (it is a place to look at the reader, not to work in the parts database),
// and a Saves tab that exists nowhere else.
//
// IT SHIPS IN PROD'S CODE, which is the only way it survives. sandbox-promote.sh resets the
// checkout to prod's HEAD, so a tab written only in the sandbox's working tree is gone with
// the next reset — and the whole point of the saves is that they outlive one. The same
// arrangement the demo has had all along: one build flag, one repo.
export const IS_SANDBOX = import.meta.env.VITE_NOTICE_SANDBOX === '1';

// DEV, localhost:5173 against the dev backend on prod's data (scripts/start.sh --dev). Said in
// the tab's title, so a tab of it is not taken for prod's.
export const IS_DEV = import.meta.env.VITE_NOTICE_DEV === '1';

axios.defaults.baseURL = API_PREFIX || undefined;
